import { useState, useEffect, ChangeEvent, useRef, useCallback, RefObject } from 'react';
import ImageCard from './ImageCard';
import GalleryImageCard from './GalleryImageCard';
import Loading from '../common/Loading';
import { DUImage } from './pages/types/DUImage';
import { apiRequest } from '../utils/authConfig';
import { useMsal, account } from '../utils/authContext';
import { Accordion, Alert, Button, Col, Form, FormControl, InputGroup, Row } from 'react-bootstrap';
import AlertDismissible from '../common/AlertDismissible';
import LoadingButton from '../common/LoadingButton';
import { getFileExtension, getHeaders, getTokenAndFetchHeaders } from '../utils';

const allowedFileTypes = ['jpg', 'jpeg', 'png', 'gif'];
interface DUFile extends File {
    lastModifiedDate: Date;
}
const Images = (props: any) => {
    const { getToken } = useMsal();
    const selectMode = props?.selectMode;
    const handleSelected = props?.handleSelected;
    const selectedItems = props.selectedItems;
    const fileInput = useRef<HTMLInputElement>(null);
    const [error, setError] = useState<string>('');
    const [isLoading, setIsLoading] = useState(false);
    const [isUploading, setIsUploading] = useState(false);
    const [fileInvalidError, setFileInvalidError] = useState(false);
    const [items, setItems]: [DUImage[], Function] = useState([]);
    const [directories, setDirectories]: [string[], Function] = useState([]);
    const [current, setCurrent]: [string, Function] = useState('/');
    const [selectedFile, setSelectedFile]: [DUFile | undefined, any] = useState();
    let folderNameInput = useRef(null);
    const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
        const extension = getFileExtension(event.target.value);
        const fileIsValid = allowedFileTypes.some((e) => e === extension.toLowerCase());
        setFileInvalidError(!fileIsValid);
        if (event.target.files && fileIsValid) {
            setSelectedFile(event.target.files[0]);
        }
    };
    const handleDelete = async () => {
        try {
            await loadContent();
        } catch (err:any) {
            handleError(err);
        }
    };
    const handleError = (err: Error) => {
        setError(err.message);
    };
    const handleUpload = async () => {
        const file = selectedFile;
        if (!file) {
            return;
        }
        setIsUploading(true);
        const formData = new FormData();
        formData.append(
            "image",
            file,
            file.name
        );
        formData.append(
            "folder",
            encodeURIComponent(current)
        )
        const api = process.env['REACT_APP_BACKEND_URL'];
        const args = { ...apiRequest, account };
        const apiKey = process.env.REACT_APP_BACKEND_KEY ? process.env.REACT_APP_BACKEND_KEY : '';
        const headers = await getTokenAndFetchHeaders(getToken, args, apiKey, null);
        try {
            await fetch(`${api}/image`, {
                method: 'POST',
                headers,
                body: formData,
            });
            setIsUploading(false);
            setSelectedFile(null);
            const toUpload = fileInput?.current;
            if (toUpload) {
                toUpload.value = '';
            }
            await loadContent();

        } catch (err: any) {
            setIsUploading(false);
            setError(error);
        }
    }
    const addFolder = (el: RefObject<HTMLInputElement>) => {
        if (el?.current?.value) {
            const newFolder = el.current.value;
            const format = newFolder.charAt(0) !== '/' ? `/${newFolder}` : newFolder;
            const folder = current === '/' ? format : `${current}${format}`;
            setDirectories([...directories, folder]);
            el.current.value = '';
        }
    };
    const fileData = () => {
        const file = selectedFile;
        if (!file) {
            return null;
        } else {
            return (<span>{file.name}</span>);
        }
    };
    const loadContent = useCallback(async () => {
        setIsLoading(true);
        const api = process.env['REACT_APP_BACKEND_URL'];
        const apiKey = process.env.REACT_APP_BACKEND_KEY ? process.env.REACT_APP_BACKEND_KEY : '';
        try {
            const headers = getHeaders(apiKey, null, null);
            const res = await fetch(`${api}/image/?folder=${encodeURIComponent(current)}`, {
                headers,
                method: 'GET',
            });
            if (!res.ok) {
                throw new Error("Failed to load images.");
            }
            const result = await res.json();
            setIsLoading(false);
            setError('');
            const { directories: dirs } = result.data;
            const shownImages = Object.keys(result.data).filter(k => k !== 'directories');
            let images: string[] = [];
            for (const k of shownImages) {
                // Current directory is always first.
                dirs.unshift(k);
                images = [...result.data[k]];
            }
            setItems(images);
            setDirectories(dirs);
        } catch (err:any) {
            setIsLoading(false);
            setError(err.toString());
        }
    }, [current]);
    useEffect(() => {
        loadContent();
    }, [loadContent]);
    return <>
        {error && <AlertDismissible variant="danger" className="mt-4">{error}</AlertDismissible>}
        <Accordion className="my-4" defaultActiveKey="1">
            <Accordion.Item eventKey="0">
                <Accordion.Header>
                    Manage Folders (Current: {current})
                </Accordion.Header>
                <Accordion.Body>
                    <ul className="list-unstyled">
                        {
                            directories.map((d: string, i: number) => <li key={`directory-${i}`}>
                                <Button variant="link" className="no-focus p-0" onClick={() => {
                                    setCurrent(d);
                                }}><span className={i === 0 ? 'fw-bold' : ''}>{d}</span></Button></li>)
                        }
                    </ul>
                    <Row>
                        <Col className="col-md-4">
                            <InputGroup className="mb-3">
                                <FormControl
                                    placeholder="Folder Name"
                                    aria-label="FolderName"
                                    aria-describedby="create-folder"
                                    ref={folderNameInput}
                                />
                                <Button onClick={() => addFolder(folderNameInput)} variant="outline-secondary">Add Folder</Button>
                            </InputGroup>
                        </Col>
                    </Row>
                </Accordion.Body>
            </Accordion.Item>
            <Accordion.Item eventKey="1">
                <Accordion.Header>
                    Upload Images
                </Accordion.Header>
                <Accordion.Body>
                    <div className="custom-file">
                        {!selectedFile && <>
                            <Row>
                                <Col className="col-md-4">
                                    {fileInvalidError && <Alert variant="danger">Choose a valid file ({allowedFileTypes.join(', ')}).</Alert>}
                                    <Form>
                                        <Form.Label>Choose an Image </Form.Label>
                                        <Form.Control
                                            onChange={handleChange}
                                            type="file"
                                        />
                                        <div className="text-end"><small>({allowedFileTypes.join(', ')})</small></div>
                                    </Form>
                                </Col>
                            </Row>
                        </>}
                        {selectedFile && <div>{fileData()} <LoadingButton loading={isUploading} className="btn btn-primary ms-4" onClick={handleUpload}> Upload </LoadingButton></div>}
                    </div>
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
        <>
            {!isLoading && selectMode && items.map((image: DUImage) => <GalleryImageCard image={image} key={image.thumbnail} selectedItems={selectedItems} handleSelected={handleSelected} onDelete={handleDelete} />)}
            {!isLoading && !selectMode && items.map((image: DUImage) => <ImageCard image={image} key={image.thumbnail} onDelete={handleDelete} onError={handleError} />)}
            {isLoading && <Loading />}
            {!isLoading && !items.length && <span>No records</span>}
        </>
    </>;
};

export default Images;
